import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { TERMS_VERSION, TERMS_EFFECTIVE_DATE } from "@/pages/terms";
import { ScrollText, AlertTriangle } from "lucide-react";

const SUPPORT_EMAIL = "support@eb5veras.com";

interface TermsAcceptanceGateProps {
  queryKey: string;
}

export function TermsAcceptanceGate({ queryKey }: TermsAcceptanceGateProps) {
  const { toast } = useToast();
  const [checked, setChecked] = useState(false);
  // Counts failed save attempts across manual retries. TanStack Query's
  // `mutation.failureCount` resets to 0 at the start of every `mutate()` call,
  // so it never reflects "this keeps failing" across separate retry clicks —
  // we track that ourselves to decide when to escalate to a support fallback.
  const [failedAttempts, setFailedAttempts] = useState(0);

  const mutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/accept-terms", {});
    },
    onSuccess: () => {
      setFailedAttempts(0);
      queryClient.setQueryData(
        [queryKey],
        (prev: Record<string, unknown> | null | undefined) => {
          if (!prev) return prev;
          return { ...prev, needsTermsAcceptance: false };
        },
      );
      toast({
        title: "Terms accepted",
        description: "You're all set. Welcome back!",
      });
    },
    onError: () => {
      setFailedAttempts((n) => n + 1);
      toast({
        title: "Something went wrong",
        description: "We couldn't record your acceptance. Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={true}>
      <DialogContent
        className={[
          "bg-brand-card-bg border-[#2a2a35] text-white",
          /* Flex column layout: scrollable body + pinned footer button */
          "flex flex-col p-0 overflow-hidden",
          /* Cap to viewport on all screen sizes; override base h-[100dvh] mobile default */
          "h-auto max-h-[90dvh]",
          /* Comfortable width on all sizes */
          "w-[calc(100%-2rem)] max-w-md rounded-lg",
          /* Kill the close button — terms must be accepted */
          "[&>button]:hidden",
          /* Task #2807 — gentle fade + scale-in instead of the default slide-up
             "pop". Auth has fully resolved before this mounts (App gates the
             shell on the auth check), so this reads as a calm, intentional ease
             rather than something snapping into the middle of the page. */
          "duration-500 ease-out",
          "data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0",
          "data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95",
        ].join(" ")}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* ── Scrollable body: header + summary + checkbox ─────────────────── */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-4 space-y-4">
          {/* ── Header ──────────────────────────────────────────────────────── */}
          <DialogHeader className="space-y-2 pb-1">
            <div className="flex items-center justify-center sm:justify-start gap-2.5">
              <span className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-primary/15">
                <ScrollText className="w-5 h-5 text-primary" />
              </span>
              <DialogTitle
                className="font-heading text-lg sm:text-xl leading-snug text-white"
                data-testid="text-terms-gate-title"
              >
                Terms & Privacy updated
              </DialogTitle>
            </div>
            <DialogDescription className="text-[#999] text-sm leading-relaxed text-center sm:text-left">
              We've updated our Terms of Service and Privacy Policy. Please
              review and accept before continuing.
            </DialogDescription>
            <p
              className="text-xs text-[#777] text-center sm:text-left"
              data-testid="text-terms-gate-version"
            >
              Version {TERMS_VERSION} · Effective {TERMS_EFFECTIVE_DATE}
            </p>
          </DialogHeader>

          {/* ── Scrollable summary ────────────────────────────────────────── */}
          <div className="bg-[#07111e] border border-[#1e2d3d] rounded-lg p-4 text-sm text-[#aaa] leading-relaxed space-y-2 max-h-36 overflow-y-auto">
            <p>
              By accepting, you confirm that you have read and agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
              >
                Terms of Service
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80 transition-colors"
              >
                Privacy Policy
              </a>
              .
            </p>
            <p>
              These govern your use of the Veras platform, including how we
              collect and process your information in connection with EB-5
              investment services.
            </p>
          </div>

          {/* ── Checkbox ──────────────────────────────────────────────────── */}
          <div className="flex items-start gap-3 py-1">
            <Checkbox
              id="terms-accept-checkbox"
              checked={checked}
              onCheckedChange={(v) => setChecked(Boolean(v))}
              className="mt-0.5 min-w-[18px] border-[#555] data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              data-testid="checkbox-accept-terms"
            />
            <Label
              htmlFor="terms-accept-checkbox"
              className="text-sm text-[#ccc] leading-relaxed cursor-pointer select-none"
            >
              I have read and agree to the Terms of Service and Privacy Policy
            </Label>
          </div>
        </div>

        {/* ── Pinned CTA footer — never scrolls away ────────────────────────── */}
        <div className="px-6 pb-6 pt-4 border-t border-[#2a2a35] space-y-3">
          {/* Inline error + recovery affordance. The button below re-enables
              automatically once the mutation settles (isPending → false), so a
              failed attempt never leaves the user stuck — they can retry. After
              a repeated failure we surface a support fallback so a persistent
              outage isn't a dead end. */}
          {mutation.isError && (
            <div
              role="alert"
              className="flex items-start gap-2.5 rounded-lg border border-[#5c2b2b] bg-[#2a1414] p-3 text-sm text-[#f3b8b8]"
              data-testid="text-terms-error"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#e57373]" />
              <div className="space-y-1 leading-relaxed">
                <p>We couldn't record your acceptance. Please try again.</p>
                {failedAttempts > 1 && (
                  <p data-testid="text-terms-support-fallback">
                    Still having trouble? Contact us at{" "}
                    <a
                      href={`mailto:${SUPPORT_EMAIL}`}
                      className="font-medium text-[#f3b8b8] underline underline-offset-2 hover:text-white transition-colors"
                      data-testid="link-terms-support"
                    >
                      {SUPPORT_EMAIL}
                    </a>
                    .
                  </p>
                )}
              </div>
            </div>
          )}

          <Button
            onClick={() => mutation.mutate()}
            disabled={!checked || mutation.isPending}
            className="w-full h-11 bg-primary hover:bg-primary/90 text-brand-dark-bg font-semibold text-sm"
            data-testid="button-accept-terms"
          >
            {mutation.isPending
              ? "Saving…"
              : mutation.isError
                ? "Try again"
                : "Accept & Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
